import styled, {css} from 'styled-components';

import BaseCloseIcon from '@edna/icons/close.svg';
import KeyboardArrowDownIcon from '@edna/icons/keyboardArrowDown.svg';
import BaseSearchIcon from '@edna/icons/search.svg';
import {hexToRgba} from '@edna/utils';

import BaseOption from '../Option';
import {TInputProps, mixins} from '../styles';

type TValueProps = TTheme & {
  multiple?: boolean;
  disabled?: boolean;
};

type TDeleteButtonProps = TTheme & {
  disabled?: boolean;
};

type THeadProps = {
  isOpen: boolean;
  isPlacementBottom: boolean;
  disabled?: boolean;
  multiple?: boolean;
  isInvalid?: boolean;
};

type TIconProps = {
  open: boolean;
};

const VALUE_SIZE = 24;
const ARROW_SIZE = 16;

const getIconTransform = ({open}: TIconProps) => css`
  transform: rotate(${open ? '180deg' : 0});
  transition: transform ${({theme}) => theme.animation.duration}ms;
`;

export const ArrowIcon = styled(KeyboardArrowDownIcon)<TIconProps>`
  ${mixins.wh(`${ARROW_SIZE}px`)}
  ${getIconTransform}
`;

const getArrowIconWrapperStyle = ({theme: {dimensions, spacing}}: TTheme) => {
  const top = (dimensions.controlHeightM - ARROW_SIZE - 2 * dimensions.borderWidth) / 2;

  return mixins.position({
    position: 'absolute',
    top: `${top}px`,
    right: spacing.s3,
  });
};

export const ArrowIconWrapper = styled.div`
  display: flex;
  ${getArrowIconWrapperStyle}
`;

export const Select = styled.div`
  position: relative;
  min-height: ${({theme}) => theme.dimensions.controlHeightM}px;
  padding: 0;
  outline: 0;
  user-select: none;
`;

const getHeadOpenStyle = ({
  theme: {
    boxShadow,
    borderRadius: {br1},
    palette,
    branding,
  },
  isOpen,
  isInvalid,
}: TTheme & THeadProps) => {
  if (isOpen) {
    return css`
      border-color: ${branding.content.primary};
      border-radius: ${br1};
      box-shadow: ${boxShadow.input};
      &:focus {
        border-color: ${isInvalid ? palette.red400 : branding.content.primary};
      }
    `;
  }

  return '';
};

const getHeadStyle = ({theme: {dimensions, spacing}, disabled}: TTheme & THeadProps) => {
  const top = (dimensions.controlHeightM - 2 * dimensions.borderWidth - VALUE_SIZE) / 2;

  return css`
    min-height: ${dimensions.controlHeightM}px;
    max-height: 152px;
    padding: ${top}px ${spacing.s7} 0 14px;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    overflow-y: auto;
  `;
};

export const Head = styled.div<TInputProps & THeadProps>`
  ${mixins.input}
  position: relative;
  height: auto;
  ${getHeadStyle}
  ${getHeadOpenStyle}
`;

export type TPlaceholderProps = {
  disabled?: boolean;
  readOnly?: boolean;
};

export const Placeholder = styled.div<TPlaceholderProps>`
  line-height: ${VALUE_SIZE}px;
  padding: 0 ${({theme}) => theme.spacing.s2};
`;

const getValueLabelStyle = ({multiple, theme: {spacing}}: TValueProps) => css`
  margin-right: ${multiple ? 0 : spacing.s7};
  padding-left: ${spacing.s2};
`;

const getArrowIconStyle = () => css`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

export const ParentLabel = styled.div`
  line-height: 1.1;
  width: max-content;
  max-width: 100%;
  ${({theme: {palette, spacing, borderRadius}}) => css`
    margin-bottom: ${spacing.s1};
    padding: ${spacing.s1} ${spacing.s2};
    background-color: ${palette.grey500};
    color: ${palette.white};
    border: 1px solid ${palette.grey600};
    border-radius: ${borderRadius.br2};
  `}
`;

export const BackToParentButton = styled.button`
  all: unset;
  ${mixins.flexAlign('flex-start', 'center')}
  ${({theme: {spacing, palette, dimensions}}) => css`
    width: calc(100% - ${spacing.s4});
    min-height: ${dimensions.controlHeightM}px;
    padding-left: ${spacing.s4};
    column-gap: ${spacing.s4};
    color: ${palette.black};
  `}

  &:hover {
    cursor: pointer;
  }

  & svg {
    ${getArrowIconStyle};
    align-self: flex-start;
    margin-top: 10px;
  }
`;

export const ValueLabel = styled.div<TValueProps>`
  ${getValueLabelStyle}
  ${mixins.truncate}
`;

const getDeleteButtonStyle = ({theme, disabled}: TDeleteButtonProps) => {
  if (disabled) {
    return css`
      cursor: not-allowed;
    `;
  }

  return css`
    color: ${theme.palette.grey400};
    &:hover {
      cursor: pointer;
      color: ${theme.palette.black};
    }
  `;
};

export const DeleteButton = styled.button`
  ${mixins.wh('22px')}
  ${mixins.flexAlign('center')}
  flex-shrink: 0;
  padding: 0;
  color: inherit;
  border: none;
  background-color: transparent;
  outline: none;
  ${getDeleteButtonStyle}
`;

const getValueBackgroundColor = ({theme: {palette}, multiple, disabled}: TValueProps) => {
  if (multiple) {
    if (disabled) {
      return hexToRgba(palette.black, 0.1);
    }

    return palette.grey200;
  }

  return 'transparent';
};

const getValueStyle = ({theme: {borderRadius}}: TValueProps) => css`
  min-height: ${VALUE_SIZE}px;
  border-radius: ${borderRadius.br2};
  background-color: ${getValueBackgroundColor};
`;

const getMultipleValueStyle = ({
  theme: {branding, palette, spacing, borderRadius},
  multiple,
}: TValueProps) => {
  if (multiple) {
    return css`
      display: inline-flex;
      max-width: calc(100% - ${spacing.s2});
      margin: 0 ${spacing.s2} ${spacing.s1} 0;
      background-color: ${branding.backgrounds.primary};
      border: 1px solid ${palette.grey200};
      border-radius: ${borderRadius.br2};
    `;
  }

  return css`
    display: flex;
    max-width: max-content;
  `;
};

export const Value = styled.div<TValueProps>`
  ${getValueStyle}
  ${getMultipleValueStyle}
  align-items: center;
`;

type TOverlayProps = {
  hasScroll: boolean;
  isPlacementBottom: boolean;
};

export const Overlay = styled.div<TOverlayProps>`
  ${({
    theme: {
      borderRadius: {br1},
      zIndex,
      boxShadow,
    },
    hasScroll,
    isPlacementBottom,
  }) => css`
    ${mixins.paper}
    z-index: ${zIndex.dropdown};
    ${mixins.position({
      position: 'absolute',
      left: 0,
      top: isPlacementBottom ? '100%' : undefined,
      bottom: isPlacementBottom ? undefined : '100%',
    })}
    outline: 0;
    width: 100%;
    overflow: ${hasScroll ? 'auto' : 'hidden'};
    box-shadow: ${boxShadow.dropdown};
    border-radius: ${isPlacementBottom ? `0 0 ${br1} ${br1}` : `${br1} ${br1} 0 0`};
  `}
`;

export const DeleteIcon = styled(BaseCloseIcon)`
  ${mixins.wh('14px')}
`;

type TOptionProps = {
  isCurrent?: boolean;
};

export const Option = styled(BaseOption)<TOptionProps>`
  white-space: pre-line;
  justify-content: space-between;
  min-height: ${({theme: {dimensions}}) => `${dimensions.controlHeightM}px`};
  ${({theme: {branding}, isCurrent}) =>
    isCurrent
      ? css`
          background-color: ${branding.backgrounds.secondary};
        `
      : ''}

  & svg {
    ${getArrowIconStyle}
  }
`;

export const OverlayFooter = styled.div`
  ${mixins.position({position: 'sticky', bottom: 0})}

  ${({theme: {spacing, branding}}) => css`
    padding: ${`${spacing.s2} ${spacing.s4}`};
    background-color: ${branding.backgrounds.primary};
  `}
`;

export const SpinnerWrapper = styled.div`
  ${mixins.flexAlign('center')}
  min-height: 50px;
`;

type TSearchOptionsWrapperProps = {visibleElementCount: number};

export const SearchOptionsWrapper = styled.div<TSearchOptionsWrapperProps>`
  width: 100%;
  overflow: auto;
  ${({visibleElementCount, theme: {dimensions, borderRadius}}) => css`
    max-height: ${visibleElementCount * dimensions.controlHeightM}px;
    border-radius: 0 0 ${borderRadius.br1} ${borderRadius.br1};
  `}
`;

export const SearchIcon = styled(BaseSearchIcon)`
  ${mixins.wh('16px')}
  ${mixins.position({
    position: 'absolute',
    top: 'calc(50% - 8px)',
    right: '16px',
  })}
`;

const getInputStyle = ({theme: {dimensions, spacing, branding}}: TTheme) => css`
  ${mixins.wh(`calc(100% - ${spacing.s4})`, `calc(${dimensions.controlHeightM}px - ${spacing.s2})`)}
  margin: ${spacing.s2};
  padding: 0 ${spacing.s7} 0 ${spacing.s2};
  &:focus {
    border-color: ${branding.content.primary};
    box-shadow: none;
  }
`;

export const Input = styled.input`
  ${mixins.input}
  ${getInputStyle}
`;

export const SearchInput = styled.div`
  position: relative;
`;
